import { QueryClient } from '@tanstack/react-query';

/**
 * App-wide TanStack Query client.
 *
 * Lives in its own module (not inside App.tsx) so non-React code paths
 * — optimistic mutation builders, the resilient-mutation wrapper, the
 * sync engine bindings — can read cached values synchronously without
 * having to call `useQueryClient()` in places where hooks aren't legal.
 *
 * Defaults explained:
 *   - `staleTime: 5min` + `gcTime: 24h` — stale-but-usable data is way
 *     better than a blank flash. Cache stays warm in memory + persisted
 *     storage for a full day so an offline launch always renders.
 *   - `retry: false on network errors` — useResilientMutation already
 *     queues network failures in the outbox; double-retrying just
 *     wastes battery.
 *   - `refetchOnReconnect: 'always'` — fresh server state pulls in the
 *     moment the network is back.
 *   - `networkMode: 'offlineFirst'` — cached data renders instantly
 *     even with a dead network. Without this, a successful cached
 *     query would still flash a skeleton on mount until either the
 *     network reply lands or the user gives up.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 24 * 60 * 60 * 1000,
      retry: (failureCount, error) => {
        const msg = error instanceof Error ? error.message : String(error);
        if (/network|fetch|offline/i.test(msg)) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
      networkMode: 'offlineFirst',
    },
    mutations: {
      networkMode: 'offlineFirst',
    },
  },
});
